import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import {
  ChatBubbleLeftRightIcon,
  UserIcon,
  BeakerIcon,
  EyeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  TagIcon,
  XMarkIcon,
  CalculatorIcon,
  LinkIcon,
  CheckCircleIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  ArrowsPointingOutIcon,
  CreditCardIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import {
  obtenerClientes,
  obtenerCristales,
  obtenerLentesContacto,
  obtenerArmazonesDisponibles,
  obtenerDescripcionesCristales,
  crearDescripcionCristal,
  actualizarDescripcionCristal,
  eliminarDescripcionCristal,
  asignarCaracteristicaProductos,
  enviarPresupuestoWhatsApp,
  obtenerMiOptica,
  obtenerPrintConfig,
  guardarDescuentoEfectivo,
  guardarDescuentoArmazones,
  guardarCuotasOptica,
} from '../api/backend_v2';
import {
  cargarDescuentoEfectivoSeguro,
  cargarDescuentoArmazonesSeguro,
  calcularPrecioEfectivoConDescuento,
  redondearMiles,
  DESCUENTO_EFECTIVO_DEFAULT,
  DESCUENTO_ARMAZONES_DEFAULT,
  formatearTextoCuotas,
  cargarCuotasOpticaSeguro,
  OPCIONES_CUOTAS_SISTEMA,
  esArmazonCacic,
  filtrarCuotasParaPresupuesto,
  filtrarCuotasConTope,
  resolverCondicionesArmazon,
  encontrarProveedorArmazon,
} from '../utils/precios';
import { obtenerProveedores } from '../utils/proveedores';
import { abrirWhatsApp, sanitizarMensajeWhatsApp } from '../utils/whatsappHelper';
import PresupuestoCardImagen, { parsearItemsDescripcion } from '../components/PresupuestoCardImagen';

const formatearPrecio = (precio) => {
  if (!precio || isNaN(precio)) return '$0';
  return `$${Math.round(parseFloat(precio)).toLocaleString('es-AR')}`;
};

const TIPOS_VISION = ['Monofocal', 'Bifocal', 'Multifocal', 'Ocupacional'];

const Presupuestos = () => {
  const [tabActiva, setTabActiva] = useState('cotizador'); // 'cotizador' | 'descripciones'

  // --- ESTADOS: CLIENTE Y SELECCIÓN DE RECEPTOR ---
  const [modoCliente, setModoCliente] = useState('buscar'); // 'buscar' | 'manual'
  const [clientesList, setClientesList] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [nombreManual, setNombreManual] = useState('');
  const [telefonoManual, setTelefonoManual] = useState('');

  // --- ESTADOS: CRISTALES Y LENTES DE CONTACTO ---
  const [subtabOptica, setSubtabOptica] = useState('cristales'); // 'cristales' | 'contacto'
  const [cristales, setCristales] = useState([]);
  const [filtroTipoVision, setFiltroTipoVision] = useState('Todos');
  const [busquedaCristal, setBusquedaCristal] = useState('');
  const [cristalesSeleccionados, setCristalesSeleccionados] = useState([]); // [{ cristalId, cristal, descripcionId, notas }]

  // Lentes de Contacto
  const [lentesContacto, setLentesContacto] = useState([]);
  const [filtroTipoLenteContacto, setFiltroTipoLenteContacto] = useState('Todos');
  const [busquedaLenteContacto, setBusquedaLenteContacto] = useState('');
  const [lentesContactoSeleccionadas, setLentesContactoSeleccionadas] = useState([]); // [{ lenteId, lente, notas }]

  // --- ESTADOS: ARMAZONES ---
  const [armazones, setArmazones] = useState([]);
  const [busquedaArmazon, setBusquedaArmazon] = useState('');
  const [armazonesSeleccionados, setArmazonesSeleccionados] = useState([]); // [{ id, ...armazon, descripcionId }]

  // --- ESTADOS: DESCRIPCIONES DE CRISTALES / ARMAZONES ---
  const [descripciones, setDescripciones] = useState([]);
  const [categoriaDesc, setCategoriaDesc] = useState('cristal'); // 'cristal' | 'armazon'
  const [modalDescripcionOpen, setModalDescripcionOpen] = useState(false);
  const [descripcionEditando, setDescripcionEditando] = useState(null);
  const [formDescripcion, setFormDescripcion] = useState({
    tipo_producto: 'cristal',
    tipo_vision: 'Monofocal',
    marca: '',
    titulo: '',
    descripcion: '',
  });

  // --- ESTADO INTERNO DEL MODAL: BÚSQUEDA Y SELECCIÓN DE PRODUCTOS ---
  const [busquedaProdModal, setBusquedaProdModal] = useState('');
  const [filtroVisionModal, setFiltroVisionModal] = useState('Monofocal');
  const [itemsSeleccionadosModal, setItemsSeleccionadosModal] = useState([]);

  // --- ESTADOS: PRECIOS Y DESCUENTOS ---
  const [descuentoOptica, setDescuentoOptica] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [descuentoArmazon, setDescuentoArmazon] = useState(DESCUENTO_ARMAZONES_DEFAULT);
  const [cuotasSeleccionadas, setCuotasSeleccionadas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [overrideEfectivo, setOverrideEfectivo] = useState('');
  const [validezDias, setValidezDias] = useState(7);
  const [notasPresupuesto, setNotasPresupuesto] = useState('');
  const [mostrarResumenCotizacion, setMostrarResumenCotizacion] = useState(false);
  const [guardandoPredeterminados, setGuardandoPredeterminados] = useState(false);

  // --- ESTADOS: BRANDING ÓPTICA E IMAGEN ---
  const [opticaInfo, setOpticaInfo] = useState(null);
  const [printConfig, setPrintConfig] = useState(null);
  const [modoPrevisualizacion, setModoPrevisualizacion] = useState('tarjeta'); // 'tarjeta' | 'texto'
  const [modalImagenOpen, setModalImagenOpen] = useState(false);
  const [vistaPreviaImagenSrc, setVistaPreviaImagenSrc] = useState(null);
  const [generandoImagen, setGenerandoImagen] = useState(false);
  const cardRef = useRef(null);

  // --- ESTADOS: CARGA Y ENVÍO ---
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingForm, setSavingForm] = useState(false);

  // Cargar todos los datos
  const cargarTodosLosDatos = useCallback(async () => {
    try {
      const [descuentoVal, descArmVal, cuotasRes, clientesRes, cristalesRes, lentesRes, armazonesRes, descRes, opticaRes, printConfRes, provsRes] = await Promise.all([
        cargarDescuentoEfectivoSeguro(),
        cargarDescuentoArmazonesSeguro(),
        cargarCuotasOpticaSeguro(),
        obtenerClientes().catch(() => ({ clientes: [] })),
        obtenerCristales().catch(() => ({ cristales: [] })),
        obtenerLentesContacto().catch(() => ({ lentes: [] })),
        obtenerArmazonesDisponibles().catch(() => ({ armazones: [] })),
        obtenerDescripcionesCristales().catch(() => ({ descripciones: [] })),
        obtenerMiOptica().catch(() => null),
        obtenerPrintConfig().catch(() => null),
        obtenerProveedores().catch(() => []),
      ]);

      setDescuentoOptica(descuentoVal);
      setDescuentoArmazon(descArmVal);
      setCuotasSeleccionadas(Array.isArray(cuotasRes?.cuotas_disponibles) ? cuotasRes.cuotas_disponibles : []);
      setClientesList(Array.isArray(clientesRes?.clientes) ? clientesRes.clientes : Array.isArray(clientesRes) ? clientesRes : []);
      setCristales(Array.isArray(cristalesRes?.cristales) ? cristalesRes.cristales : Array.isArray(cristalesRes) ? cristalesRes : []);
      setLentesContacto(Array.isArray(lentesRes?.lentes) ? lentesRes.lentes : Array.isArray(lentesRes) ? lentesRes : []);
      setArmazones(Array.isArray(armazonesRes?.armazones) ? armazonesRes.armazones : Array.isArray(armazonesRes) ? armazonesRes : []);
      setDescripciones(Array.isArray(descRes?.descripciones) ? descRes.descripciones : []);
      setProveedores(Array.isArray(provsRes) ? provsRes : (provsRes?.proveedores || []));
      if (opticaRes) setOpticaInfo(opticaRes);
      if (printConfRes) setPrintConfig(printConfRes);
    } catch (error) {
      console.error('Error al cargar datos de presupuestos:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await cargarTodosLosDatos();
      setLoading(false);
    };
    init();

    // Listener para actualizar el logo si es modificado en SuperAdmin u otra vista
    const handleLogoUpdated = () => {
      cargarTodosLosDatos();
    };
    window.addEventListener('optica-logo-updated', handleLogoUpdated);
    return () => {
      window.removeEventListener('optica-logo-updated', handleLogoUpdated);
    };
  }, [cargarTodosLosDatos]);

  // Clientes filtrados para autocomplete
  const clientesFiltrados = useMemo(() => {
    if (!busquedaCliente.trim()) return clientesList.slice(0, 8);
    const term = busquedaCliente.toLowerCase().trim();
    return clientesList.filter(
      (c) =>
        (c.nombre || '').toLowerCase().includes(term) ||
        (c.apellido || '').toLowerCase().includes(term) ||
        (c.telefono || '').toLowerCase().includes(term)
    ).slice(0, 8);
  }, [clientesList, busquedaCliente]);

  // Cristales filtrados por tipo de visión y búsqueda
  const cristalesFiltrados = useMemo(() => {
    return cristales.filter((c) => {
      const coincideTipo = filtroTipoVision === 'Todos' || c.tipo_vision === filtroTipoVision;
      const term = busquedaCristal.toLowerCase().trim();
      const coincideTexto =
        !term ||
        (c.marca || '').toLowerCase().includes(term) ||
        (c.producto || '').toLowerCase().includes(term) ||
        (c.material || '').toLowerCase().includes(term) ||
        (c.tratamiento || '').toLowerCase().includes(term);
      return coincideTipo && coincideTexto;
    });
  }, [cristales, filtroTipoVision, busquedaCristal]);

  // Tipos de lentes de contacto
  const TIPOS_LENTES_CONTACTO = ['Monofocal', 'Tórica', 'Multifocal', 'Cosmética / Color'];

  // Lentes de contacto filtradas
  const lentesContactoFiltradas = useMemo(() => {
    return lentesContacto.filter((l) => {
      let coincideTipo = true;
      if (filtroTipoLenteContacto !== 'Todos') {
        if (filtroTipoLenteContacto === 'Cosmética / Color') {
          coincideTipo = Boolean(l.son_esteticas);
        } else {
          coincideTipo = (l.tipo_vision || '').toLowerCase().includes(filtroTipoLenteContacto.toLowerCase());
        }
      }
      const term = busquedaLenteContacto.toLowerCase().trim();
      const coincideTexto =
        !term ||
        (l.marca || '').toLowerCase().includes(term) ||
        (l.tipo_vision || '').toLowerCase().includes(term) ||
        (l.color || '').toLowerCase().includes(term) ||
        (l.rango || '').toLowerCase().includes(term);
      return coincideTipo && coincideTexto;
    });
  }, [lentesContacto, filtroTipoLenteContacto, busquedaLenteContacto]);

  const toggleSeleccionLenteContacto = (lente) => {
    setLentesContactoSeleccionadas((prev) => {
      const existe = prev.some((item) => item.lenteId === lente.id);
      if (existe) {
        return prev.filter((item) => item.lenteId !== lente.id);
      } else {
        return [...prev, { lenteId: lente.id, lente, notas: '' }];
      }
    });
  };

  const setNotaParaLenteContacto = (lenteId, notas) => {
    setLentesContactoSeleccionadas((prev) =>
      prev.map((item) => (item.lenteId === lenteId ? { ...item, notas } : item))
    );
  };

  // Armazones filtrados
  const armazonesFiltrados = useMemo(() => {
    if (!busquedaArmazon.trim()) return armazones.slice(0, 15);
    const term = busquedaArmazon.toLowerCase().trim();
    return armazones.filter(
      (a) =>
        (a.marca || '').toLowerCase().includes(term) ||
        (a.modelo || '').toLowerCase().includes(term) ||
        (a.color || '').toLowerCase().includes(term)
    ).slice(0, 15);
  }, [armazones, busquedaArmazon]);

  // Manejadores de Cristales Seleccionados (Auto-carga descripcion_id)
  const toggleSeleccionCristal = (cristal) => {
    setCristalesSeleccionados((prev) => {
      const existe = prev.some((item) => item.cristalId === cristal.id);
      if (existe) {
        return prev.filter((item) => item.cristalId !== cristal.id);
      } else {
        const autoDescId = cristal.descripcion_id ? String(cristal.descripcion_id) : '';
        return [...prev, { cristalId: cristal.id, cristal, descripcionId: autoDescId, notas: '' }];
      }
    });
  };

  const setDescripcionParaCristal = (cristalId, descripcionId) => {
    setCristalesSeleccionados((prev) =>
      prev.map((item) => (item.cristalId === cristalId ? { ...item, descripcionId } : item))
    );
  };

  // Manejadores de Armazones Seleccionados (Auto-carga descripcion_id)
  const toggleSeleccionArmazon = (armazon) => {
    setArmazonesSeleccionados((prev) => {
      const existe = prev.some((item) => item.id === armazon.id);
      if (existe) {
        return prev.filter((item) => item.id !== armazon.id);
      } else {
        const autoDescId = armazon.descripcion_id ? String(armazon.descripcion_id) : '';
        return [...prev, { ...armazon, descripcionId: autoDescId }];
      }
    });
  };

  const setDescripcionParaArmazon = (armazonId, descripcionId) => {
    setArmazonesSeleccionados((prev) =>
      prev.map((item) => (item.id === armazonId ? { ...item, descripcionId } : item))
    );
  };

  // Cálculos de Totales
  const subtotalCristalesPublico = useMemo(() => {
    return cristalesSeleccionados.reduce((acc, item) => acc + (parseFloat(item.cristal.precio_publico) || 0), 0);
  }, [cristalesSeleccionados]);

  const subtotalArmazonesPublico = useMemo(() => {
    return armazonesSeleccionados.reduce((acc, item) => acc + (parseFloat(item.precio_publico) || 0), 0);
  }, [armazonesSeleccionados]);

  const subtotalLentesContactoPublico = useMemo(() => {
    return lentesContactoSeleccionadas.reduce((acc, item) => acc + (parseFloat(item.lente.precio_publico) || 0), 0);
  }, [lentesContactoSeleccionadas]);

  const totalPublico = useMemo(() => {
    return subtotalCristalesPublico + subtotalArmazonesPublico + subtotalLentesContactoPublico;
  }, [subtotalCristalesPublico, subtotalArmazonesPublico, subtotalLentesContactoPublico]);

  // Detección de condiciones comerciales para armazones (por proveedor o marcas con condiciones)
  const condicionesArmazones = useMemo(() => {
    return armazonesSeleccionados.map((a) => resolverCondicionesArmazon(a, proveedores, descuentoArmazon));
  }, [armazonesSeleccionados, proveedores, descuentoArmazon]);

  const tieneArmazonesCacic = useMemo(() => {
    return armazonesSeleccionados.some((a) => {
      const cond = resolverCondicionesArmazon(a, proveedores, descuentoArmazon);
      return cond.tieneCondicionEspecial || esArmazonCacic(a);
    });
  }, [armazonesSeleccionados, proveedores, descuentoArmazon]);

  // Si hay armazones con tope de cuotas por proveedor o marcas, se calcula el tope más restrictivo
  const topeCuotasArmazones = useMemo(() => {
    const topes = condicionesArmazones
      .map((c) => c.cuotasMaximas)
      .filter((m) => m != null && Number.isFinite(m));
    return topes.length > 0 ? Math.min(...topes) : null;
  }, [condicionesArmazones]);

  const cuotasEfectivasPresupuesto = useMemo(() => {
    return filtrarCuotasConTope(cuotasSeleccionadas, topeCuotasArmazones);
  }, [cuotasSeleccionadas, topeCuotasArmazones]);

  // Cálculo de precio efectivo (Opción A: ítem por ítem considerando reglas de proveedor)
  const totalEfectivoCalculado = useMemo(() => {
    if (totalPublico <= 0) return 0;
    // Cristales: descuento general de la óptica
    let efCristales = 0;
    if (subtotalCristalesPublico > 0) {
      efCristales = redondearMiles(subtotalCristalesPublico * (1 - descuentoOptica / 100));
    }

    // Armazones: cálculo ítem por ítem según condición de su proveedor o descuento de armazón
    let efArmazones = 0;
    armazonesSeleccionados.forEach((a) => {
      const p = parseFloat(a.precio_publico) || 0;
      if (p > 0) {
        const cond = resolverCondicionesArmazon(a, proveedores, descuentoArmazon);
        const desc = cond.tieneCondicionEspecial
          ? cond.descuento
          : (esArmazonCacic(a) ? descuentoArmazon : descuentoOptica);
        efArmazones += redondearMiles(p * (1 - desc / 100));
      }
    });

    // Lentes de contacto: descuento general de la óptica
    let efLentes = 0;
    if (subtotalLentesContactoPublico > 0) {
      efLentes = redondearMiles(subtotalLentesContactoPublico * (1 - descuentoOptica / 100));
    }

    return efCristales + efArmazones + efLentes;
  }, [
    totalPublico,
    subtotalCristalesPublico,
    subtotalArmazonesPublico,
    subtotalLentesContactoPublico,
    armazonesSeleccionados,
    proveedores,
    descuentoOptica,
    descuentoArmazon,
  ]);

  const totalEfectivoFinal = useMemo(() => {
    if (overrideEfectivo !== '' && !isNaN(parseFloat(overrideEfectivo))) {
      return parseFloat(overrideEfectivo);
    }
    return totalEfectivoCalculado;
  }, [overrideEfectivo, totalEfectivoCalculado]);

  // Generación del texto para WhatsApp
  const mensajeWhatsApp = useMemo(() => {
    const destinatario = modoCliente === 'buscar'
      ? (clienteSeleccionado ? `${clienteSeleccionado.nombre} ${clienteSeleccionado.apellido}`.trim() : 'Cliente')
      : (nombreManual.trim() || 'Cliente');

    let texto = `*PRESUPUESTO - ÓPTICA*\n`;
    texto += `Hola *${destinatario}*, te compartimos el presupuesto solicitado:\n\n`;

    if (cristalesSeleccionados.length > 0) {
      texto += `👓 *CRISTALES:*\n`;
      cristalesSeleccionados.forEach((item, index) => {
        const c = item.cristal;
        const tipoLabel = c.tipo_vision || 'Cristal';
        const marcaLabel = c.marca ? ` (${c.marca})` : '';
        const prodLabel = c.producto ? ` - ${c.producto}` : '';
        const matLabel = c.material ? ` (${c.material})` : '';
        const tratLabel = c.tratamiento ? ` + ${c.tratamiento}` : '';
        const stockLabel = c.stock ? ' [Stock]' : ' [Laboratorio]';

        texto += `${index + 1}. *${tipoLabel}*${marcaLabel}${prodLabel}${matLabel}${tratLabel}${stockLabel}\n`;

        // Buscar si tiene plantilla de descripción asignada (auto o manual)
        if (item.descripcionId) {
          const descObj = descripciones.find((d) => d.id === parseInt(item.descripcionId, 10));
          if (descObj) {
            const itemsDesc = parsearItemsDescripcion(descObj.descripcion);
            const tituloLimpio = (descObj.titulo || '').replace(/^[-•*]\s*/, '').trim();
            if (itemsDesc.length > 1) {
              texto += `   ℹ️ _${tituloLimpio}_:\n`;
              itemsDesc.forEach((it) => {
                texto += `      • ${it}\n`;
              });
            } else {
              texto += `   ℹ️ _${tituloLimpio}_: ${itemsDesc[0] || descObj.descripcion}\n`;
            }
          }
        }
        if (item.notas) {
          texto += `   📝 _${item.notas}_\n`;
        }
        texto += `   Precio de Lista: ${formatearPrecio(c.precio_publico)}\n\n`;
      });
    }

    if (armazonesSeleccionados.length > 0) {
      texto += `🖼️ *ARMAZONES:*\n`;
      armazonesSeleccionados.forEach((a, index) => {
        const marca = a.marca || 'Armazón';
        const modelo = a.modelo ? ` - Mod. ${a.modelo}` : '';
        const color = a.color ? ` (${a.color})` : '';
        texto += `${index + 1}. *${marca}*${modelo}${color}\n`;

        // Buscar si el armazón tiene descripción asignada
        if (a.descripcionId) {
          const descObj = descripciones.find((d) => d.id === parseInt(a.descripcionId, 10));
          if (descObj) {
            const itemsDesc = parsearItemsDescripcion(descObj.descripcion);
            const tituloLimpio = (descObj.titulo || '').replace(/^[-•*]\s*/, '').trim();
            if (itemsDesc.length > 1) {
              texto += `   ℹ️ _${tituloLimpio}_:\n`;
              itemsDesc.forEach((it) => {
                texto += `      • ${it}\n`;
              });
            } else {
              texto += `   ℹ️ _${tituloLimpio}_: ${itemsDesc[0] || descObj.descripcion}\n`;
            }
          }
        }
        texto += `   Precio de Lista: ${formatearPrecio(a.precio_publico)}\n\n`;
      });
    }

    if (lentesContactoSeleccionadas.length > 0) {
      texto += `👁️ *LENTES DE CONTACTO:*\n`;
      lentesContactoSeleccionadas.forEach((item, index) => {
        const l = item.lente;
        const marcaLabel = l.marca ? `*${l.marca}*` : '*Lente de Contacto*';
        const tipoLabel = l.tipo_vision ? ` - ${l.tipo_vision}` : '';
        const colorLabel = l.son_esteticas && l.color ? ` (Color: ${l.color})` : '';
        const rangoLabel = l.rango ? ` [Rango: ${l.rango}]` : '';

        texto += `${index + 1}. ${marcaLabel}${tipoLabel}${colorLabel}${rangoLabel}\n`;
        if (item.notas) {
          texto += `   _Detalle:_ ${item.notas}\n`;
        }
        texto += `   Precio de Lista: ${formatearPrecio(l.precio_publico)}\n\n`;
      });
    }

    const textoCuotas = formatearTextoCuotas(cuotasEfectivasPresupuesto);
    const tieneSol = armazonesSeleccionados.some((a) => a.sol);
    const etiquetaArmazon = tieneSol ? 'armazón / anteojo de sol' : 'armazón';

    if (mostrarResumenCotizacion) {
      texto += `----------------------------------------\n`;
      texto += `💰 *RESUMEN DE COTIZACIÓN:*\n`;
      texto += `• Total Precio de Lista / Tarjeta: *${formatearPrecio(totalPublico)}*\n`;
      if (cristalesSeleccionados.length > 0 && armazonesSeleccionados.length > 0 && tieneArmazonesCacic) {
        texto += `• *TOTAL EFECTIVO / CONTADO*: *${formatearPrecio(totalEfectivoFinal)}* (con ${descuentoOptica}% desc. en cristales y ${descuentoArmazon}% en ${etiquetaArmazon})\n`;
      } else if (armazonesSeleccionados.length > 0 && tieneArmazonesCacic) {
        texto += `• *TOTAL EFECTIVO / CONTADO*: *${formatearPrecio(totalEfectivoFinal)}* (con ${descuentoArmazon}% desc. en ${etiquetaArmazon})\n`;
      } else {
        texto += `• *TOTAL EFECTIVO / CONTADO*: *${formatearPrecio(totalEfectivoFinal)}* (con ${descuentoOptica}% desc. aplicado)\n`;
      }
      if (textoCuotas) {
        texto += `• *Financiación con tarjeta:* ${textoCuotas}\n`;
      }
      texto += `\n`;
    } else {
      if (descuentoOptica > 0 || descuentoArmazon > 0 || textoCuotas) {
        texto += `----------------------------------------\n`;
        texto += `💳 *CONDICIONES DE PAGO:*\n`;
        if (cristalesSeleccionados.length > 0 && armazonesSeleccionados.length > 0 && tieneArmazonesCacic) {
          if (descuentoOptica > 0) {
            texto += `• Hasta *${descuentoOptica}% en efectivo en cristales*\n`;
          }
          if (descuentoArmazon > 0) {
            texto += `• Hasta *${descuentoArmazon}% en efectivo en ${etiquetaArmazon}*\n`;
          }
        } else if (armazonesSeleccionados.length > 0 && tieneArmazonesCacic) {
          if (descuentoArmazon > 0) {
            texto += `• Hasta *${descuentoArmazon}% en efectivo en ${etiquetaArmazon}*\n`;
          }
        } else if (descuentoOptica > 0) {
          texto += `• Hasta *${descuentoOptica}% en efectivo*\n`;
        }
        if (textoCuotas) {
          texto += `• *${textoCuotas}*\n`;
        }
        texto += `\n`;
      }
    }

    if (notasPresupuesto.trim()) {
      texto += `📌 *Observaciones:* ${notasPresupuesto.trim()}\n\n`;
    }

    texto += `⏱️ Presupuesto válido por *${validezDias} días*.\n`;
    texto += `¡Quedamos a tu disposición por cualquier consulta!`;

    return texto;
  }, [
    modoCliente,
    clienteSeleccionado,
    nombreManual,
    cristalesSeleccionados,
    lentesContactoSeleccionadas,
    armazonesSeleccionados,
    descripciones,
    totalPublico,
    totalEfectivoFinal,
    descuentoOptica,
    descuentoArmazon,
    tieneArmazonesCacic,
    cuotasEfectivasPresupuesto,
    notasPresupuesto,
    validezDias,
    mostrarResumenCotizacion,
  ]);

  // Teléfono limpio del destinatario
  const telefonoDestino = useMemo(() => {
    let raw = modoCliente === 'buscar' ? (clienteSeleccionado?.telefono || '') : telefonoManual;
    return raw.replace(/[^\d+]/g, '');
  }, [modoCliente, clienteSeleccionado, telefonoManual]);

  // Acción: Copiar Mensaje
  const handleCopiarMensaje = async () => {
    try {
      await navigator.clipboard.writeText(sanitizarMensajeWhatsApp(mensajeWhatsApp));
      Swal.fire({
        icon: 'success',
        title: 'Mensaje copiado',
        text: 'El presupuesto fue copiado al portapapeles.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (_err) {
      Swal.fire('Error', 'No se pudo copiar el texto al portapapeles', 'error');
    }
  };

  // Acción: Enviar por WhatsApp
  const handleEnviarWhatsApp = async () => {
    if (!telefonoDestino) {
      Swal.fire('Atención', 'Por favor ingresa un número de teléfono válido para enviar', 'warning');
      return;
    }

    if (cristalesSeleccionados.length === 0 && armazonesSeleccionados.length === 0) {
      Swal.fire('Atención', 'Selecciona al menos 1 cristal o 1 armazón para presupuestar', 'warning');
      return;
    }

    setSending(true);

    try {
      // 1. Abrir WhatsApp en nueva pestaña con encoding seguro directo de API
      abrirWhatsApp(telefonoDestino, mensajeWhatsApp);

      // 2. Notificar al backend via API
      await enviarPresupuestoWhatsApp({
        telefono: telefonoDestino,
        mensaje: sanitizarMensajeWhatsApp(mensajeWhatsApp),
        cliente_nombre: modoCliente === 'buscar' ? clienteSeleccionado?.nombre : nombreManual,
      }).catch(() => {});

      Swal.fire({
        icon: 'success',
        title: 'WhatsApp iniciado',
        text: 'Se abrió la conversación de WhatsApp con el presupuesto preparado.',
      });
    } catch (error) {
      console.error('Error al enviar WhatsApp:', error);
      Swal.fire('Error', 'No se pudo procesar el envío de WhatsApp', 'error');
    } finally {
      setSending(false);
    }
  };

  // --- GENERACIÓN DE IMAGEN DEL PRESUPUESTO (JPG) ---
  const obtenerCanvasPresupuesto = async () => {
    const nodo = cardRef.current;
    if (!nodo) {
      throw new Error('No se encontró el elemento para generar la imagen.');
    }

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    return await html2canvas(nodo, {
      scale: 2.5,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
    });
  };

  const handleDescargarImagenJPG = async () => {
    if (cristalesSeleccionados.length === 0 && armazonesSeleccionados.length === 0) {
      Swal.fire('Atención', 'Selecciona al menos 1 cristal o 1 armazón para generar la imagen del presupuesto', 'warning');
      return;
    }

    setGenerandoImagen(true);
    try {
      const canvas = await obtenerCanvasPresupuesto();
      const nombreDestinatario = modoCliente === 'buscar'
        ? (clienteSeleccionado ? `${clienteSeleccionado.nombre}_${clienteSeleccionado.apellido}` : 'Cliente')
        : (nombreManual.trim() || 'Cliente');

      const fechaStr = new Date().toISOString().slice(0, 10);
      const nombreLimpio = nombreDestinatario.replace(/[^a-zA-Z0-9_-]/g, '_');
      const enlace = document.createElement('a');
      enlace.href = canvas.toDataURL('image/jpeg', 0.95);
      enlace.download = `Presupuesto_${nombreLimpio}_${fechaStr}.jpg`;
      enlace.click();

      Swal.fire({
        icon: 'success',
        title: '¡Imagen JPG descargada!',
        text: 'El presupuesto en formato JPG de alta calidad se descargó correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error al generar JPG:', err);
      Swal.fire('Error', err.message || 'No se pudo generar la imagen del presupuesto', 'error');
    } finally {
      setGenerandoImagen(false);
    }
  };

  const handleCopiarImagenPortapapeles = async () => {
    if (cristalesSeleccionados.length === 0 && armazonesSeleccionados.length === 0) {
      Swal.fire('Atención', 'Selecciona al menos 1 cristal o 1 armazón para copiar el presupuesto', 'warning');
      return;
    }

    setGenerandoImagen(true);
    try {
      const canvas = await obtenerCanvasPresupuesto();

      canvas.toBlob(async (blob) => {
        if (!blob) {
          throw new Error('No se pudo procesar la imagen.');
        }

        try {
          if (navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
            Swal.fire({
              icon: 'success',
              title: '¡Imagen copiada al portapapeles!',
              text: 'Ya puedes presionar Ctrl + V (Pegar) en WhatsApp Web o en tu chat para enviar la imagen del presupuesto.',
              timer: 3000,
              showConfirmButton: false,
            });
          } else {
            throw new Error('El navegador no soporta escribir imágenes en el portapapeles');
          }
        } catch (clipErr) {
          console.warn('Clipboard write fallback to download:', clipErr);
          const enlace = document.createElement('a');
          enlace.href = canvas.toDataURL('image/jpeg', 0.95);
          enlace.download = `Presupuesto_${new Date().toISOString().slice(0, 10)}.jpg`;
          enlace.click();

          Swal.fire({
            icon: 'info',
            title: 'Imagen JPG descargada',
            text: 'Tu navegador no permite copiar imágenes directamente. Se descargó el archivo JPG para que puedas adjuntarlo en WhatsApp.',
          });
        } finally {
          setGenerandoImagen(false);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Error al copiar imagen:', err);
      Swal.fire('Error', err.message || 'No se pudo copiar la imagen', 'error');
      setGenerandoImagen(false);
    }
  };

  const handleAbrirModalVistaPreviaImagen = async () => {
    if (cristalesSeleccionados.length === 0 && armazonesSeleccionados.length === 0) {
      Swal.fire('Atención', 'Selecciona al menos 1 cristal o 1 armazón para previsualizar el presupuesto', 'warning');
      return;
    }

    setGenerandoImagen(true);
    try {
      const canvas = await obtenerCanvasPresupuesto();
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      setVistaPreviaImagenSrc(dataUrl);
      setModalImagenOpen(true);
    } catch (err) {
      console.error('Error al previsualizar imagen:', err);
      Swal.fire('Error', err.message || 'No se pudo generar la vista previa', 'error');
    } finally {
      setGenerandoImagen(false);
    }
  };

  // --- MODAL UNIFICADO: CREAR / EDITAR CARACTERÍSTICA + BÚSQUEDA Y ASIGNACIÓN DE PRODUCTOS ---
  const handleAbrirModalCrearDesc = () => {
    setDescripcionEditando(null);
    setFormDescripcion({
      tipo_producto: categoriaDesc,
      tipo_vision: categoriaDesc === 'cristal' ? 'Monofocal' : 'General',
      marca: '',
      titulo: '',
      descripcion: '',
    });
    setFiltroVisionModal(categoriaDesc === 'cristal' ? 'Monofocal' : 'Todos');
    setBusquedaProdModal('');
    setItemsSeleccionadosModal([]);
    setModalDescripcionOpen(true);
  };

  const handleAbrirModalEditarDesc = (desc) => {
    setDescripcionEditando(desc);
    const tipoProd = desc.tipo_producto || 'cristal';
    setFormDescripcion({
      tipo_producto: tipoProd,
      tipo_vision: desc.tipo_vision || 'Monofocal',
      marca: desc.marca || '',
      titulo: desc.titulo || '',
      descripcion: desc.descripcion || '',
    });
    setFiltroVisionModal(desc.tipo_vision || 'Monofocal');
    setBusquedaProdModal('');

    // Pre-seleccionar los productos que ya tienen esta característica asignada
    if (tipoProd === 'cristal') {
      const asignados = cristales.filter((c) => c.descripcion_id === desc.id).map((c) => c.id);
      setItemsSeleccionadosModal(asignados);
    } else {
      const asignados = armazones.filter((a) => a.descripcion_id === desc.id).map((a) => a.id);
      setItemsSeleccionadosModal(asignados);
    }

    setModalDescripcionOpen(true);
  };

  // Productos filtrados dentro del modal
  const productosModalFiltrados = useMemo(() => {
    const esCristal = formDescripcion.tipo_producto === 'cristal';
    const term = busquedaProdModal.toLowerCase().trim();

    if (esCristal) {
      return cristales.filter((c) => {
        const coincideVision = filtroVisionModal === 'Todos' || c.tipo_vision === filtroVisionModal;
        const coincideTexto =
          !term ||
          (c.marca || '').toLowerCase().includes(term) ||
          (c.producto || '').toLowerCase().includes(term) ||
          (c.material || '').toLowerCase().includes(term);
        return coincideVision && coincideTexto;
      });
    } else {
      return armazones.filter((a) => {
        return (
          !term ||
          (a.marca || '').toLowerCase().includes(term) ||
          (a.modelo || '').toLowerCase().includes(term) ||
          (a.color || '').toLowerCase().includes(term)
        );
      });
    }
  }, [formDescripcion.tipo_producto, cristales, armazones, filtroVisionModal, busquedaProdModal]);

  const toggleSeleccionItemModal = (itemId) => {
    setItemsSeleccionadosModal((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleSeleccionarTodosModal = () => {
    const idsVisibles = productosModalFiltrados.map((p) => p.id);
    const todosSeleccionados = idsVisibles.every((id) => itemsSeleccionadosModal.includes(id));

    if (todosSeleccionados) {
      setItemsSeleccionadosModal((prev) => prev.filter((id) => !idsVisibles.includes(id)));
    } else {
      setItemsSeleccionadosModal((prev) => Array.from(new Set([...prev, ...idsVisibles])));
    }
  };

  const handleGuardarDescripcionIntegrado = async (e) => {
    e.preventDefault();
    if (!formDescripcion.titulo.trim() || !formDescripcion.descripcion.trim()) {
      Swal.fire('Error', 'El título y la descripción son requeridos', 'error');
      return;
    }

    setSavingForm(true);

    try {
      let descIdTarget = null;

      if (descripcionEditando) {
        await actualizarDescripcionCristal(descripcionEditando.id, formDescripcion);
        descIdTarget = descripcionEditando.id;
      } else {
        const res = await crearDescripcionCristal(formDescripcion);
        descIdTarget = res?.descripcion?.id;
      }

      // Asignación masiva en lote a los productos seleccionados en el modal
      if (descIdTarget) {
        await asignarCaracteristicaProductos({
          tipo_producto: formDescripcion.tipo_producto,
          descripcion_id: descIdTarget,
          item_ids: itemsSeleccionadosModal,
        }).catch(() => {});
      }

      Swal.fire({
        icon: 'success',
        title: 'Característica guardada',
        text: `Se guardó "${formDescripcion.titulo}" y se vinculó a ${itemsSeleccionadosModal.length} productos.`,
      });

      setModalDescripcionOpen(false);
      await cargarTodosLosDatos();
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo guardar la característica', 'error');
    } finally {
      setSavingForm(false);
    }
  };

  const handleEliminarDescripcion = async (desc) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar característica?',
      text: `Se borrará "${desc.titulo}". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (confirm.isConfirmed) {
      try {
        await eliminarDescripcionCristal(desc.id);
        Swal.fire('Eliminado', 'La descripción fue eliminada', 'success');
        await cargarTodosLosDatos();
      } catch (err) {
        Swal.fire('Error', err.message || 'Error al eliminar la descripción', 'error');
      }
    }
  };

  // Filtrado de lista de descripciones en pestaña
  const descripcionesFiltradas = useMemo(() => {
    return descripciones.filter((d) => (d.tipo_producto || 'cristal') === categoriaDesc);
  }, [descripciones, categoriaDesc]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white p-8 rounded-xl shadow-sm text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Cargando módulo de presupuestos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header de la sección */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <CalculatorIcon className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Presupuestos</h1>
              <p className="text-sm text-gray-500">
                Cotizador de cristales y armazones con envío por WhatsApp y asignación persistente de características.
              </p>
            </div>
          </div>
        </div>

        {/* Pestañas */}
        <div className="flex bg-gray-100 p-1 rounded-xl self-start sm:self-center">
          <button
            type="button"
            onClick={() => setTabActiva('cotizador')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              tabActiva === 'cotizador'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            Cotizador WhatsApp
          </button>
          <button
            type="button"
            onClick={() => setTabActiva('descripciones')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              tabActiva === 'descripciones'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <TagIcon className="h-4 w-4" />
            Características y Asignación
          </button>
        </div>
      </div>

      {/* PESTAÑA 1: COTIZADOR */}
      {tabActiva === 'cotizador' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Panel Izquierdo: Selección de ítems (Col 7) */}
          <div className="lg:col-span-7 space-y-6">
            {/* 1. Selección de Destinatario */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                  1. Destinatario del Presupuesto
                </h2>
                <div className="flex bg-gray-100 p-1 rounded-lg text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setModoCliente('buscar')}
                    className={`px-3 py-1 rounded-md transition-all ${
                      modoCliente === 'buscar' ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600'
                    }`}
                  >
                    Buscar Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setModoCliente('manual')}
                    className={`px-3 py-1 rounded-md transition-all ${
                      modoCliente === 'manual' ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600'
                    }`}
                  >
                    Ingreso Manual
                  </button>
                </div>
              </div>

              {modoCliente === 'buscar' ? (
                <div className="space-y-3">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, apellido o teléfono..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={busquedaCliente}
                      onChange={(e) => setBusquedaCliente(e.target.value)}
                    />
                  </div>

                  {clienteSeleccionado ? (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 p-3 rounded-xl">
                      <div>
                        <p className="font-semibold text-blue-900 text-sm">
                          {clienteSeleccionado.nombre} {clienteSeleccionado.apellido}
                        </p>
                        <p className="text-xs text-blue-700">📱 {clienteSeleccionado.telefono || 'Sin teléfono'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setClienteSeleccionado(null)}
                        className="text-xs text-red-600 hover:text-red-800 font-semibold px-2 py-1 bg-white rounded-lg border border-red-200 shadow-xs"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                      {clientesFiltrados.length === 0 ? (
                        <p className="p-3 text-xs text-gray-500 text-center">No se encontraron clientes</p>
                      ) : (
                        clientesFiltrados.map((cli) => (
                          <button
                            key={cli.id}
                            type="button"
                            onClick={() => {
                              setClienteSeleccionado(cli);
                              setBusquedaCliente('');
                            }}
                            className="w-full p-2.5 text-left hover:bg-gray-50 flex items-center justify-between text-xs transition-colors"
                          >
                            <span className="font-medium text-gray-800">
                              {cli.nombre} {cli.apellido}
                            </span>
                            <span className="text-gray-500 font-mono">{cli.telefono}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Nombre del destinatario</label>
                    <input
                      type="text"
                      placeholder="Ej: Laura Pérez"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={nombreManual}
                      onChange={(e) => setNombreManual(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Teléfono WhatsApp</label>
                    <input
                      type="text"
                      placeholder="Ej: +5491122334455"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={telefonoManual}
                      onChange={(e) => setTelefonoManual(e.target.value)}
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <input
                  type="checkbox"
                  id="mostrarResumenCotizacion"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                  checked={mostrarResumenCotizacion}
                  onChange={(e) => setMostrarResumenCotizacion(e.target.checked)}
                />
                <label htmlFor="mostrarResumenCotizacion" className="text-xs font-semibold text-gray-700 cursor-pointer select-none">
                  Mostrar resumen de cotización (Totales) en el presupuesto
                </label>
              </div>
            </div>

            

            {/* 2. Selección de Cristales y Lentes de Contacto */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5 text-blue-600 shrink-0" />
                  <h2 className="text-base font-bold text-gray-900">
                    2. Graduación Óptica y Lentes
                  </h2>
                </div>

                {/* Sub-pestañas: Cristales vs Lentes de Contacto */}
                <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold self-start sm:self-center">
                  <button
                    type="button"
                    onClick={() => setSubtabOptica('cristales')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                      subtabOptica === 'cristales'
                        ? 'bg-white text-blue-700 shadow-xs font-bold'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <span>Cristales</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      cristalesSeleccionados.length > 0 ? 'bg-blue-100 text-blue-800 font-bold' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {cristalesSeleccionados.length}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSubtabOptica('contacto')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                      subtabOptica === 'contacto'
                        ? 'bg-white text-emerald-700 shadow-xs font-bold'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <span>Lentes de Contacto</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      lentesContactoSeleccionadas.length > 0 ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {lentesContactoSeleccionadas.length}
                    </span>
                  </button>
                </div>
              </div>

              {subtabOptica === 'cristales' ? (
                <>
                  {/* Filtros de cristales */}
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {['Todos', ...TIPOS_VISION].map((tipo) => (
                        <button
                          key={tipo}
                          type="button"
                          onClick={() => setFiltroTipoVision(tipo)}
                          className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                            filtroTipoVision === tipo
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tipo}
                        </button>
                      ))}
                    </div>

                    <input
                      type="text"
                      placeholder="Buscar cristal por marca, producto, material..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={busquedaCristal}
                      onChange={(e) => setBusquedaCristal(e.target.value)}
                    />
                  </div>

                  {/* Lista de Cristales */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {cristalesFiltrados.length === 0 ? (
                      <p className="p-4 text-xs text-gray-500 text-center">No hay cristales que coincidan con la búsqueda</p>
                    ) : (
                      cristalesFiltrados.map((c) => {
                        const estaSeleccionado = cristalesSeleccionados.some((item) => item.cristalId === c.id);
                        const tieneDescAsignada = Boolean(c.descripcion_id);
                        return (
                          <div
                            key={c.id}
                            onClick={() => toggleSeleccionCristal(c)}
                            className={`p-3 text-xs flex items-center justify-between cursor-pointer transition-colors ${
                              estaSeleccionado ? 'bg-blue-50/70 border-l-4 border-blue-600' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={estaSeleccionado}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <div>
                                <p className="font-bold text-gray-900 flex items-center gap-1.5">
                                  {c.tipo_vision} {c.marca ? `- ${c.marca}` : ''} {c.producto ? `(${c.producto})` : ''}
                                  {tieneDescAsignada && (
                                    <span className="bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <TagIcon className="h-3 w-3" /> Auto
                                    </span>
                                  )}
                                </p>
                                <p className="text-gray-500">
                                  {c.material || 'Estándar'} {c.tratamiento ? ` | ${c.tratamiento}` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="text-right flex items-center gap-3">
                              <span
                                className={`px-2 py-0.5 rounded-md font-semibold text-[11px] ${
                                  c.stock
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}
                              >
                                {c.stock ? 'Stock' : 'Laboratorio'}
                              </span>
                              <div>
                                <p className="font-bold text-gray-900">{formatearPrecio(c.precio_publico)}</p>
                                {c.precio_efectivo && (
                                  <p className="text-[11px] text-emerald-600 font-medium">
                                    Ef: {formatearPrecio(c.precio_efectivo)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Asignación/Ajuste de descripciones en cristales seleccionados */}
                  {cristalesSeleccionados.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                      <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                        Características asociadas a los Cristales Elegidos
                      </h3>
                      {cristalesSeleccionados.map((item, idx) => {
                        const descDisponibles = descripciones.filter(
                          (d) => (d.tipo_producto || 'cristal') === 'cristal'
                        );

                        return (
                          <div key={item.cristalId} className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-bold text-blue-900">
                                #{idx + 1} {item.cristal.tipo_vision} {item.cristal.marca || ''}
                              </span>
                              <span className="text-gray-500">{formatearPrecio(item.cristal.precio_publico)}</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <select
                                className="w-full p-2 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                                value={item.descripcionId || ''}
                                onChange={(e) => setDescripcionParaCristal(item.cristalId, e.target.value)}
                              >
                                <option value="">-- Sin característica --</option>
                                {descDisponibles.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    [{d.tipo_vision}] {d.titulo} {d.marca ? `(${d.marca})` : ''}
                                  </option>
                                ))}
                              </select>

                              <input
                                type="text"
                                placeholder="Nota adicional opcional"
                                className="w-full p-2 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                                value={item.notas || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setCristalesSeleccionados((prev) =>
                                    prev.map((it) => (it.cristalId === item.cristalId ? { ...it, notas: val } : it))
                                  );
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Filtros de lentes de contacto */}
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {['Todos', ...TIPOS_LENTES_CONTACTO].map((tipo) => (
                        <button
                          key={tipo}
                          type="button"
                          onClick={() => setFiltroTipoLenteContacto(tipo)}
                          className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                            filtroTipoLenteContacto === tipo
                              ? 'bg-emerald-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tipo}
                        </button>
                      ))}
                    </div>

                    <input
                      type="text"
                      placeholder="Buscar lente de contacto por marca, tipo, color o rango..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      value={busquedaLenteContacto}
                      onChange={(e) => setBusquedaLenteContacto(e.target.value)}
                    />
                  </div>

                  {/* Lista de Lentes de Contacto */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {lentesContactoFiltradas.length === 0 ? (
                      <p className="p-4 text-xs text-gray-500 text-center">No hay lentes de contacto que coincidan con la búsqueda</p>
                    ) : (
                      lentesContactoFiltradas.map((l) => {
                        const estaSeleccionada = lentesContactoSeleccionadas.some((item) => item.lenteId === l.id);
                        return (
                          <div
                            key={l.id}
                            onClick={() => toggleSeleccionLenteContacto(l)}
                            className={`p-3 text-xs flex items-center justify-between cursor-pointer transition-colors ${
                              estaSeleccionada ? 'bg-emerald-50/70 border-l-4 border-emerald-600' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={estaSeleccionada}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                              />
                              <div>
                                <p className="font-bold text-gray-900 flex items-center gap-1.5">
                                  {l.marca || 'Lente de Contacto'} {l.tipo_vision ? `- ${l.tipo_vision}` : ''}
                                  {l.son_esteticas && (
                                    <span className="bg-pink-100 text-pink-800 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                                      {l.color ? `Color: ${l.color}` : 'Cosmética'}
                                    </span>
                                  )}
                                </p>
                                <p className="text-gray-500">
                                  {l.rango ? `Rango: ${l.rango}` : 'Graduación según prescripción'}
                                </p>
                              </div>
                            </div>

                            <div className="text-right flex items-center gap-3">
                              <div>
                                <p className="font-bold text-gray-900">{formatearPrecio(l.precio_publico)}</p>
                                {l.precio_efectivo && (
                                  <p className="text-[11px] text-emerald-600 font-medium">
                                    Ef: {formatearPrecio(l.precio_efectivo)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Notas / Detalle para Lentes de Contacto Seleccionadas */}
                  {lentesContactoSeleccionadas.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                      <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                        Detalle / Graduación de Lentes de Contacto Elegidas
                      </h3>
                      {lentesContactoSeleccionadas.map((item, idx) => (
                        <div key={item.lenteId} className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-200/60 space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-emerald-950">
                              #{idx + 1} {item.lente.marca} - {item.lente.tipo_vision} {item.lente.color ? `(${item.lente.color})` : ''}
                            </span>
                            <span className="text-gray-600 font-semibold">{formatearPrecio(item.lente.precio_publico)}</span>
                          </div>

                          <input
                            type="text"
                            placeholder="Graduación o nota opcional (Ej: OD -2.50 / OI -2.75, Curva 8.6)"
                            className="w-full p-2 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500"
                            value={item.notas || ''}
                            onChange={(e) => setNotaParaLenteContacto(item.lenteId, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 3. Selección de Armazones */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <EyeIcon className="h-5 w-5 text-blue-600" />
                  3. Armazones (Selección múltiple)
                </h2>
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                  {armazonesSeleccionados.length} seleccionado(s)
                </span>
              </div>

              <input
                type="text"
                placeholder="Buscar armazón por marca, modelo, color..."
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={busquedaArmazon}
                onChange={(e) => setBusquedaArmazon(e.target.value)}
              />

              <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {armazonesFiltrados.length === 0 ? (
                  <p className="p-4 text-xs text-gray-500 text-center">No hay armazones disponibles</p>
                ) : (
                  armazonesFiltrados.map((arm) => {
                    const estaSeleccionado = armazonesSeleccionados.some((item) => item.id === arm.id);
                    const tieneDescAsignada = Boolean(arm.descripcion_id);
                    return (
                      <div
                        key={arm.id}
                        onClick={() => toggleSeleccionArmazon(arm)}
                        className={`p-3 text-xs flex items-center justify-between cursor-pointer transition-colors ${
                          estaSeleccionado ? 'bg-blue-50/70 border-l-4 border-blue-600' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={estaSeleccionado}
                            onChange={() => {}}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                          />
                          <div>
                            <p className="font-bold text-gray-900 flex items-center gap-1.5">
                              {arm.marca} {arm.modelo ? `- Mod. ${arm.modelo}` : ''}
                              {tieneDescAsignada && (
                                <span className="bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                  <TagIcon className="h-3 w-3" /> Auto
                                </span>
                              )}
                            </p>
                            <p className="text-gray-500">{arm.color || 'Sin especificación de color'}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatearPrecio(arm.precio_publico)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Selección manual/ajuste de característica para armazones elegidos */}
              {armazonesSeleccionados.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Características asociadas a Armazones Elegidos
                  </h3>
                  {armazonesSeleccionados.map((item, idx) => {
                    const descArmazones = descripciones.filter(
                      (d) => (d.tipo_producto || 'cristal') === 'armazon'
                    );

                    return (
                      <div key={item.id} className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-blue-900">
                            #{idx + 1} {item.marca} {item.modelo ? `- ${item.modelo}` : ''}
                          </span>
                          <span className="text-gray-500">{formatearPrecio(item.precio_publico)}</span>
                        </div>

                        <select
                          className="w-full p-2 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                          value={item.descripcionId || ''}
                          onChange={(e) => setDescripcionParaArmazon(item.id, e.target.value)}
                        >
                          <option value="">-- Sin característica --</option>
                          {descArmazones.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.titulo} {d.marca ? `(${d.marca})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 4. Ajustes y Opciones del Presupuesto */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-blue-600" />
                4. Opciones de Cotización
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Descuento Cristales (%)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={descuentoOptica}
                    onChange={(e) => setDescuentoOptica(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Descuento Armazón (%)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={descuentoArmazon}
                    onChange={(e) => setDescuentoArmazon(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Override Efectivo ($) <span className="text-[10px] text-gray-400 font-normal">(Opcional)</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Sustituir total"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={overrideEfectivo}
                    onChange={(e) => setOverrideEfectivo(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Validez (Días)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={validezDias}
                    onChange={(e) => setValidezDias(parseInt(e.target.value, 10) || 7)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Observaciones adicionales</label>
                <input
                  type="text"
                  placeholder="Ej: Incluye estuche y paño microfibra de regalo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={notasPresupuesto}
                  onChange={(e) => setNotasPresupuesto(e.target.value)}
                />
              </div>

              {/* Cuotas sin interés ofrecidas por la óptica */}
              <div className="pt-3 border-t border-gray-100 space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <CreditCardIcon className="h-4 w-4 text-blue-600 shrink-0" />
                    <span className="text-xs font-bold text-gray-900">
                      Cuotas sin interés ofrecidas con tarjeta:
                    </span>
                  </div>
                  {cuotasEfectivasPresupuesto.length > 0 && (
                    <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full self-start">
                      {formatearTextoCuotas(cuotasEfectivasPresupuesto)}
                    </span>
                  )}
                </div>

                {tieneArmazonesCacic && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-xl text-xs flex items-center gap-2">
                    <SparklesIcon className="h-4 w-4 text-amber-600 shrink-0" />
                    <span>
                      {topeCuotasArmazones
                        ? `Armazones con condición comercial especial en el presupuesto: cuotas sin interés limitadas automáticamente a máx. ${topeCuotasArmazones} cuotas.`
                        : 'Armazones con condición comercial especial en el presupuesto: aplican condiciones personalizadas de proveedor.'}
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {OPCIONES_CUOTAS_SISTEMA.map((cuota) => {
                    const activa = cuotasSeleccionadas.includes(cuota);
                    return (
                      <button
                        key={cuota}
                        type="button"
                        onClick={() => {
                          setCuotasSeleccionadas((prev) => {
                            const existe = prev.includes(cuota);
                            const next = existe ? prev.filter((c) => c !== cuota) : [...prev, cuota];
                            return next.sort((a, b) => a - b);
                          });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                          activa
                            ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={activa}
                          onChange={() => {}} // Manejado por onClick del botón
                          className="h-3.5 w-3.5 rounded text-blue-600 border-gray-300 pointer-events-none"
                        />
                        <span>{cuota} cuotas</span>
                      </button>
                    );
                  })}
                  {cuotasSeleccionadas.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setCuotasSeleccionadas([])}
                      className="text-[11px] text-gray-400 hover:text-red-600 underline ml-1 cursor-pointer"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              {/* Botón para guardar condiciones oficiales de la óptica */}
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  disabled={guardandoPredeterminados}
                  onClick={async () => {
                    try {
                      setGuardandoPredeterminados(true);
                      await Promise.all([
                        guardarDescuentoEfectivo(descuentoOptica),
                        guardarDescuentoArmazones(descuentoArmazon),
                        guardarCuotasOptica(cuotasSeleccionadas),
                      ]);
                      Swal.fire({
                        icon: 'success',
                        title: 'Predeterminados guardados',
                        text: `Se guardaron ${descuentoOptica}% en cristales, ${descuentoArmazon}% en armazones y "${formatearTextoCuotas(cuotasSeleccionadas) || 'Sin cuotas'}" como configuración oficial de la óptica.`,
                        timer: 2000,
                        showConfirmButton: false,
                      });
                    } catch (err) {
                      console.error('Error al guardar condiciones:', err);
                      Swal.fire('Error', 'No se pudieron guardar las condiciones por defecto', 'error');
                    } finally {
                      setGuardandoPredeterminados(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-blue-700 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 px-3 py-1.5 rounded-xl transition-all disabled:opacity-60 cursor-pointer"
                >
                  <SparklesIcon className="h-3.5 w-3.5 text-blue-600" />
                  <span>Guardar como predeterminado de la óptica</span>
                </button>
              </div>
            </div>
          </div>

          {/* Panel Derecho: Previsualización Gráfica / WhatsApp (Col 5) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 sticky top-24 space-y-4">
              {/* Encabezado del Panel y Selector de Formato */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <PhotoIcon className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-base font-bold text-gray-900">Previsualización</h2>
                </div>

                {/* Switcher: Tarjeta Gráfica vs Texto */}
                <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-semibold self-start sm:self-center">
                  <button
                    type="button"
                    onClick={() => setModoPrevisualizacion('tarjeta')}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all ${
                      modoPrevisualizacion === 'tarjeta'
                        ? 'bg-white text-indigo-600 shadow-xs'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <PhotoIcon className="h-3.5 w-3.5" />
                    Imagen JPG
                  </button>
                  <button
                    type="button"
                    onClick={() => setModoPrevisualizacion('texto')}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all ${
                      modoPrevisualizacion === 'texto'
                        ? 'bg-white text-emerald-600 shadow-xs'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                    Texto WhatsApp
                  </button>
                </div>
              </div>

              {/* Vista Previa: Tarjeta Gráfica */}
              {modoPrevisualizacion === 'tarjeta' ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-indigo-50/70 border border-indigo-100 px-3 py-2 rounded-xl text-xs text-indigo-900">
                    <span className="font-medium flex items-center gap-1.5">
                      <SparklesIcon className="h-4 w-4 text-indigo-600 shrink-0" />
                      Diseño profesional con logo institucional
                    </span>
                    <button
                      type="button"
                      onClick={handleAbrirModalVistaPreviaImagen}
                      disabled={generandoImagen}
                      className="text-[11px] font-bold text-indigo-700 hover:text-indigo-900 bg-white px-2.5 py-1 rounded-lg shadow-2xs border border-indigo-200 flex items-center gap-1 transition-all"
                    >
                      <ArrowsPointingOutIcon className="h-3 w-3" />
                      Ampliar HD
                    </button>
                  </div>

                  {/* Contenedor interactivo de la tarjeta escalada */}
                  <div className="bg-slate-100 p-2.5 rounded-xl border border-slate-200 max-h-[440px] overflow-y-auto shadow-inner flex justify-center">
                    <div className="w-full transform origin-top" style={{ maxWidth: '100%' }}>
                      <PresupuestoCardImagen
                        optica={opticaInfo}
                        printConfig={printConfig}
                        cliente={modoCliente === 'buscar' ? clienteSeleccionado : null}
                        nombreManual={nombreManual}
                        telefonoManual={telefonoManual}
                        cristalesSeleccionados={cristalesSeleccionados}
                        lentesContactoSeleccionadas={lentesContactoSeleccionadas}
                        armazonesSeleccionados={armazonesSeleccionados}
                        descripciones={descripciones}
                        totalPublico={totalPublico}
                        totalEfectivo={totalEfectivoFinal}
                        descuentoOptica={descuentoOptica}
                        descuentoArmazon={descuentoArmazon}
                        tieneArmazonesCacic={tieneArmazonesCacic}
                        validezDias={validezDias}
                        notasPresupuesto={notasPresupuesto}
                        mostrarResumenCotizacion={mostrarResumenCotizacion}
                        cuotasSeleccionadas={cuotasEfectivasPresupuesto}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Vista Previa: Simulador WhatsApp Texto */
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                    <span>Destino:</span>
                    <span className="font-mono font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                      {telefonoDestino || 'Sin teléfono especificado'}
                    </span>
                  </div>
                  <div className="bg-[#efeae2] p-4 rounded-xl border border-[#d1c7b7] shadow-inner font-sans text-xs text-gray-800 whitespace-pre-wrap leading-relaxed max-h-[380px] overflow-y-auto">
                    <div className="bg-white p-3.5 rounded-lg shadow-xs border border-gray-200">
                      {mensajeWhatsApp}
                    </div>
                  </div>
                </div>
              )}

              {/* Desglose de totales rápido */}
              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200 space-y-1.5 text-xs">
                <div className="flex justify-between text-gray-600">
                  <span>Precio de Lista / Tarjeta:</span>
                  <span className="font-bold text-gray-900">{formatearPrecio(totalPublico)}</span>
                </div>
                <div className="flex justify-between text-emerald-700 text-sm font-bold pt-1 border-t border-gray-200">
                  <span>TOTAL CONTADO / EFECTIVO:</span>
                  <span>{formatearPrecio(totalEfectivoFinal)}</span>
                </div>
              </div>

              {/* Botones de Acción de Imagen y Envío */}
              <div className="space-y-2 pt-1">
                {/* 1. Botón Principal: Descargar JPG */}
                <button
                  type="button"
                  onClick={handleDescargarImagenJPG}
                  disabled={generandoImagen}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-sm rounded-xl shadow-md transition-all active:scale-[0.98] disabled:opacity-60"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  {generandoImagen ? 'Generando imagen JPG...' : 'Descargar Presupuesto JPG'}
                </button>

                {/* 2. Botón Copiar Imagen para pegar en WhatsApp */}
                <button
                  type="button"
                  onClick={handleCopiarImagenPortapapeles}
                  disabled={generandoImagen}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl shadow-xs transition-all active:scale-[0.98] disabled:opacity-60"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  Copiar Imagen (para pegar en WhatsApp)
                </button>

                {/* 3. Botón Iniciar WhatsApp */}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleEnviarWhatsApp}
                    disabled={sending}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-white hover:bg-gray-50 text-emerald-700 font-bold text-xs border border-emerald-300 rounded-xl shadow-xs transition-colors"
                  >
                    <PaperAirplaneIcon className="h-4 w-4 text-emerald-600" />
                    {sending ? 'Abriendo...' : 'Abrir WhatsApp'}
                  </button>

                  <button
                    type="button"
                    onClick={handleCopiarMensaje}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs border border-gray-300 rounded-xl transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />
                    Copiar Texto
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PESTAÑA 2: GESTIÓN DE CARACTERÍSTICAS Y ASIGNACIÓN */}
      {tabActiva === 'descripciones' && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <TagIcon className="h-6 w-6 text-blue-600" />
                Gestor de Características y Asignación
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Crea descripciones por tipo/marca y asígnalas masivamente a tus cristales o armazones dentro del mismo modal.
              </p>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-center">
              {/* Categoría Selector */}
              <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setCategoriaDesc('cristal')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    categoriaDesc === 'cristal' ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600'
                  }`}
                >
                  Cristales
                </button>
                <button
                  type="button"
                  onClick={() => setCategoriaDesc('armazon')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    categoriaDesc === 'armazon' ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600'
                  }`}
                >
                  Armazones
                </button>
              </div>

              <button
                type="button"
                onClick={handleAbrirModalCrearDesc}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition-all"
              >
                <PlusIcon className="h-4 w-4" />
                Nueva Característica
              </button>
            </div>
          </div>

          {/* Tabla de descripciones */}
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-left text-xs text-gray-700">
              <thead className="bg-gray-50 text-gray-900 font-bold uppercase tracking-wider border-b border-gray-200">
                <tr>
                  <th className="p-3">Categoría</th>
                  {categoriaDesc === 'cristal' && <th className="p-3">Tipo Visión</th>}
                  <th className="p-3">Marca</th>
                  <th className="p-3">Título / Nombre</th>
                  <th className="p-3">Descripción</th>
                  <th className="p-3 text-center">Productos Asignados</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {descripcionesFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-gray-500 text-xs">
                      No hay plantillas de características para {categoriaDesc === 'cristal' ? 'cristales' : 'armazones'}. ¡Haz clic en "Nueva Característica" para agregar la primera!
                    </td>
                  </tr>
                ) : (
                  descripcionesFiltradas.map((desc) => {
                    const cantAsignados = categoriaDesc === 'cristal'
                      ? cristales.filter((c) => c.descripcion_id === desc.id).length
                      : armazones.filter((a) => a.descripcion_id === desc.id).length;

                    return (
                      <tr key={desc.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 font-semibold text-gray-900 uppercase">
                          {desc.tipo_producto || 'cristal'}
                        </td>
                        {categoriaDesc === 'cristal' && (
                          <td className="p-3 font-semibold text-blue-700">
                            <span className="bg-blue-50 px-2 py-1 rounded-md border border-blue-100">
                              {desc.tipo_vision}
                            </span>
                          </td>
                        )}
                        <td className="p-3 font-medium text-gray-900">{desc.marca || 'Todas las marcas'}</td>
                        <td className="p-3 font-bold text-gray-900">{desc.titulo}</td>
                        <td className="p-3 text-gray-600 max-w-xs">{desc.descripcion}</td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              cantAsignados > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {cantAsignados} asignado(s)
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleAbrirModalEditarDesc(desc)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 font-medium"
                              title="Editar y Asignar"
                            >
                              <PencilIcon className="h-4 w-4" />
                              Editar / Asignar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEliminarDescripcion(desc)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL INTEGRADO: NUEVA / EDITAR CARACTERÍSTICA + BÚSQUEDA Y ASIGNACIÓN DE PRODUCTOS */}
      {modalDescripcionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4 pt-20 pb-8 sm:pt-24">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 space-y-4 animate-in fade-in zoom-in duration-150 max-h-[85vh] flex flex-col">
            {/* Header del Modal */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {descripcionEditando ? 'Editar Característica y Asignación' : 'Nueva Característica y Asignación'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Completa los datos de la característica y selecciona los productos a los cuales asignarla.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalDescripcionOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleGuardarDescripcionIntegrado} className="space-y-4 text-xs overflow-y-auto pr-1 flex-grow">
              {/* Bloque 1: Formulario de la Característica */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <TagIcon className="h-4 w-4 text-blue-600" /> Datos de la Característica
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-gray-700 mb-1">Categoría de Producto</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={formDescripcion.tipo_producto}
                      onChange={(e) => {
                        const nuevoTipo = e.target.value;
                        setFormDescripcion({ ...formDescripcion, tipo_producto: nuevoTipo });
                        setItemsSeleccionadosModal([]);
                      }}
                    >
                      <option value="cristal">Cristal</option>
                      <option value="armazon">Armazón</option>
                    </select>
                  </div>

                  {formDescripcion.tipo_producto === 'cristal' && (
                    <div>
                      <label className="block font-semibold text-gray-700 mb-1">Tipo de Visión</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        value={formDescripcion.tipo_vision}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormDescripcion({ ...formDescripcion, tipo_vision: val });
                          setFiltroVisionModal(val);
                        }}
                      >
                        {TIPOS_VISION.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Marca / Colección (Opcional)</label>
                  <input
                    type="text"
                    placeholder="Ej: Essilor, Ray-Ban, Genérico"
                    className="w-full p-2 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={formDescripcion.marca}
                    onChange={(e) => setFormDescripcion({ ...formDescripcion, marca: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Título de la característica</label>
                  <input
                    type="text"
                    placeholder="Ej: Antireflejo Crizal Easy / Garantía oficial de 1 año"
                    className="w-full p-2 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none font-semibold"
                    value={formDescripcion.titulo}
                    onChange={(e) => setFormDescripcion({ ...formDescripcion, titulo: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Descripción / Explicación</label>
                  <textarea
                    rows={3}
                    placeholder="Ej: Protege contra reflejos molestos, repela agua y polvo, mejorando la nitidez visual."
                    className="w-full p-2 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={formDescripcion.descripcion}
                    onChange={(e) => setFormDescripcion({ ...formDescripcion, descripcion: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Bloque 2: Asignación Directa de Productos */}
              <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-200 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <LinkIcon className="h-4 w-4 text-amber-600" /> Asignar esta Característica a Productos
                  </h4>

                  <span className="text-xs font-bold text-amber-900 bg-amber-100 px-2.5 py-1 rounded-full self-start">
                    {itemsSeleccionadosModal.length} seleccionado(s)
                  </span>
                </div>

                {/* Filtros de la lista interna */}
                <div className="space-y-2">
                  {formDescripcion.tipo_producto === 'cristal' && (
                    <div className="flex flex-wrap gap-1">
                      {['Todos', ...TIPOS_VISION].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setFiltroVisionModal(t)}
                          className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${
                            filtroVisionModal === t
                              ? 'bg-amber-600 text-white shadow-xs'
                              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <div className="relative flex-grow">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2" />
                      <input
                        type="text"
                        placeholder="Buscar por marca, producto, material, modelo..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        value={busquedaProdModal}
                        onChange={(e) => setBusquedaProdModal(e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={toggleSeleccionarTodosModal}
                      className="px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-700 font-semibold text-[11px] border border-gray-300 rounded-lg whitespace-nowrap shadow-xs"
                    >
                      Seleccionar Todos
                    </button>
                  </div>
                </div>

                {/* Lista scrollable de productos */}
                <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100 bg-white">
                  {productosModalFiltrados.length === 0 ? (
                    <p className="p-3 text-xs text-gray-500 text-center">No hay productos que coincidan con la búsqueda</p>
                  ) : (
                    productosModalFiltrados.map((p) => {
                      const estaSeleccionado = itemsSeleccionadosModal.includes(p.id);
                      const esCristal = formDescripcion.tipo_producto === 'cristal';

                      return (
                        <div
                          key={p.id}
                          onClick={() => toggleSeleccionItemModal(p.id)}
                          className={`p-2.5 text-xs flex items-center justify-between cursor-pointer transition-colors ${
                            estaSeleccionado ? 'bg-amber-50 border-l-4 border-amber-500 font-medium' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={estaSeleccionado}
                              onChange={() => {}}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 h-4 w-4"
                            />
                            <div>
                              <p className="font-bold text-gray-900 flex items-center gap-1.5">
                                {esCristal
                                  ? `${p.tipo_vision} ${p.marca ? `- ${p.marca}` : ''} ${p.producto ? `(${p.producto})` : ''}`
                                  : `${p.marca} ${p.modelo ? `- Mod. ${p.modelo}` : ''} (${p.color || 'Estándar'})`}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {esCristal
                                  ? `${p.material || 'Estándar'} ${p.tratamiento ? `| ${p.tratamiento}` : ''}`
                                  : `Precio Lista: ${formatearPrecio(p.precio_publico)}`}
                              </p>
                            </div>
                          </div>

                          <div className="text-right">
                            {esCristal && (
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  p.stock ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'
                                }`}
                              >
                                {p.stock ? 'Stock' : 'Laboratorio'}
                              </span>
                            )}
                            {p.descripcion_id && descripcionEditando && p.descripcion_id !== descripcionEditando.id && (
                              <span className="block text-[10px] text-gray-400 mt-0.5">
                                Otra asignación
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Footer / Botones */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setModalDescripcionOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingForm}
                  className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-60 flex items-center gap-1.5"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {savingForm
                    ? 'Guardando...'
                    : `Guardar Característica y Asignar (${itemsSeleccionadosModal.length})`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONTENEDOR OCULTO PARA CAPTURA DE ALTA DEFINICIÓN (680px) */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: -9999,
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        <PresupuestoCardImagen
          ref={cardRef}
          optica={opticaInfo}
          printConfig={printConfig}
          cliente={modoCliente === 'buscar' ? clienteSeleccionado : null}
          nombreManual={nombreManual}
          telefonoManual={telefonoManual}
          cristalesSeleccionados={cristalesSeleccionados}
          lentesContactoSeleccionadas={lentesContactoSeleccionadas}
          armazonesSeleccionados={armazonesSeleccionados}
          descripciones={descripciones}
          totalPublico={totalPublico}
          totalEfectivo={totalEfectivoFinal}
          descuentoOptica={descuentoOptica}
          descuentoArmazon={descuentoArmazon}
          tieneArmazonesCacic={tieneArmazonesCacic}
          validezDias={validezDias}
          notasPresupuesto={notasPresupuesto}
          mostrarResumenCotizacion={mostrarResumenCotizacion}
          cuotasSeleccionadas={cuotasEfectivasPresupuesto}
        />
      </div>

      {/* MODAL DE VISTA PREVIA HD Y EXPORTACIÓN DE IMAGEN */}
      {modalImagenOpen && vistaPreviaImagenSrc && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-xs z-[1000] flex items-center justify-center p-4 pt-20 pb-8 sm:pt-24">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-150">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-slate-50">
              <div className="flex items-center gap-2">
                <PhotoIcon className="h-5 w-5 text-indigo-600" />
                <h3 className="text-base font-bold text-gray-900">
                  Vista Previa del Presupuesto en Imagen (JPG)
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setModalImagenOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Imagen Renderizada en Scroll */}
            <div className="p-4 overflow-y-auto flex-1 bg-slate-100 flex flex-col items-center justify-start">
              <img
                src={vistaPreviaImagenSrc}
                alt="Presupuesto generado"
                className="max-w-full h-auto rounded-xl shadow-md border border-slate-200 block my-auto"
              />
            </div>

            {/* Footer con Acciones Rápidas */}
            <div className="p-4 border-t border-gray-100 bg-white flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-gray-500">
                <span>💡 Puedes copiar la imagen y pegarla directamente con <b>Ctrl + V</b> en WhatsApp Web.</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopiarImagenPortapapeles}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  Copiar Imagen
                </button>

                <button
                  type="button"
                  onClick={handleDescargarImagenJPG}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Descargar JPG
                </button>

                <button
                  type="button"
                  onClick={() => setModalImagenOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presupuestos;
